import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export const AdminGuard = () => {
  const { user, loading, demoLogin } = useAuth();
  const location = useLocation();
  const attemptedRef = useRef(false);     // persistent across renders, no re-render on set
  const [autoBusy, setAutoBusy] = useState(true);

  useEffect(() => {
    if (loading) return;                  // wait for auth context
    if (user) {                            // already signed in
      setAutoBusy(false);
      return;
    }
    if (attemptedRef.current) {            // already tried once and failed
      setAutoBusy(false);
      return;
    }
    attemptedRef.current = true;
    setAutoBusy(true);
    demoLogin()
      .catch(() => {})
      .finally(() => setAutoBusy(false));
  }, [loading, user, demoLogin]);

  if (loading || autoBusy) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] text-muted-foreground" data-testid="admin-guard-loading">
        <div className="text-center space-y-2">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          <div className="text-[11px] uppercase tracking-[0.18em]">Authenticating</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] px-6">
        <div className="max-w-md text-center space-y-3" data-testid="admin-access-denied">
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-medium">403</div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Admin access required</h1>
          <p className="text-sm text-muted-foreground">
            Your account ({user.email}) is not on the operations whitelist. Ask the platform owner to add you.
          </p>
        </div>
      </div>
    );
  }
  return <Outlet />;
};
